import { createFileRoute } from '@tanstack/react-router'
import { pageSeo } from '@/lib/seo'
import { msg } from '@lingui/core/macro'
import type { Locale } from 'shared/locales'
import { useSessionUser } from '@/hooks/use-session'
import { useResolvedTheme } from '@/hooks/use-resolved-theme'
import { trpc } from '@/main'
import { skipToken, useQuery } from '@tanstack/react-query'
import { useClientOnlyQuery } from '@/hooks/use-client-only-query'
import { useEffect, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/lib/locales'

export const Route = createFileRoute('/_landing/docs')({
  head: (ctx) =>
    pageSeo(ctx, {
      path: '/docs',
      title: msg`API documentation`,
      description: msg`Reference for the D’Anchiano API: integrate psychographic assessment of candidates, employees and positions into your own systems.`,
    }),
  component: RouteComponent,
  pendingComponent: DocsPending,
  staticData: { hideFooter: true },
})

type ScalarOptions = {
  content: string
  customCss: string
  showDeveloperTools: 'never'
  forceDarkModeState: 'light' | 'dark'
  hideDarkModeToggle: boolean
  withDefaultFonts: boolean
  telemetry: boolean
  localization: { locale: string }
  agent: { disabled: boolean }
  mcp: { disabled: boolean }
}

declare global {
  interface Window {
    Scalar?: {
      createApiReference: (elementId: string, options: ScalarOptions) => { destroy: () => void }
    }
  }
}

// Pinned: the unversioned jsdelivr path follows `latest`, so a Scalar release could restyle or break this page
// between two deploys of ours with nothing in our history to point at.
const SCALAR_VERSION = '1.69.2'

// Scalar ships its chrome (search, "Copy", "Responses"...) in es and pt but not ca: Catalan falls back to Spanish
// rather than to Scalar's English default, the nearer of the two for a Catalan reader.
const SCALAR_LOCALES: Record<Locale, string> = { 'es-ES': 'es', 'ca-ES': 'es', 'en-US': 'en', 'pt-BR': 'pt' }

let scalarScript: Promise<void> | undefined

// We're injecting scalar instead of importing the react / node library because is too big of a dependency. One
// module-level promise, so re-rendering the reference (the secret arrives after the first paint) reuses the script
// rather than mounting a second instance over the first.
function loadScalar() {
  scalarScript ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://cdn.jsdelivr.net/npm/@scalar/api-reference@${SCALAR_VERSION}`
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      // A failed load is not cached: the next attempt (a retry, a later visit) fetches the script afresh.
      script.remove()
      scalarScript = undefined
      reject(new Error('Failed to load the API reference'))
    })
    document.body.appendChild(script)
  })
  return scalarScript
}

// Scalar's theming surface is its CSS custom properties, so the page is themed by handing it the app's own tokens.
// The semantic tokens flip under the app's `.dark`, so both schemes share one mapping; the palette hues do not, so
// they are mapped once per scheme.
const customCss = `
/* app.css gives <html> \`scroll-behavior: smooth\` for the report's in-page anchors. The reference is one
   ~38,000px document, so there that rule animates an anchor jump the whole way — long enough that Scalar's
   scroll-spy rewrites the hash section by section on the way past and the link reads as broken. Jumps here
   land at once. Scoped by :has so it is this page's rule, not a global one. */
html:has(#scalar) {
  scroll-behavior: auto;
}
.scalar-app {
  --scalar-font: var(--font-sans);
  --scalar-font-code: var(--font-mono);
  --scalar-border-width: 1px;
  --scalar-radius: 6px;
  --scalar-radius-lg: var(--radius);
  --scalar-radius-xl: calc(var(--radius) + 4px);
  --scalar-sidebar-width: 17rem;
  --scalar-content-max-width: 66rem;
  /* The landing bar is sticky at h-17; without this Scalar's own sticky sidebar and search scroll under it. */
  --scalar-custom-header-height: 4.25rem;
  --scalar-bold: 700;
  --scalar-semibold: 600;
}
.light-mode, .dark-mode {
  --scalar-color-1: var(--color-foreground);
  --scalar-color-2: color-mix(in oklab, var(--color-foreground) 72%, var(--color-background));
  --scalar-color-3: var(--color-muted-foreground);
  --scalar-color-accent: var(--color-primary);
  --scalar-background-1: var(--color-background);
  --scalar-background-2: color-mix(in oklab, var(--color-muted) 55%, var(--color-background));
  --scalar-background-3: var(--color-muted);
  --scalar-background-accent: color-mix(in oklab, var(--color-primary) 12%, transparent);
  --scalar-border-color: var(--color-border);
  --scalar-link-color: var(--color-foreground);
  --scalar-link-color-hover: var(--color-primary);
  --scalar-button-1: var(--color-primary);
  --scalar-button-1-hover: var(--button-fill-hover);
  --scalar-button-1-color: var(--button-foreground);
  --scalar-sidebar-background-1: var(--color-background);
  --scalar-sidebar-color-1: var(--color-foreground);
  --scalar-sidebar-color-2: var(--color-muted-foreground);
  --scalar-sidebar-border-color: transparent;
  --scalar-sidebar-color-active: var(--color-foreground);
  --scalar-sidebar-item-hover-background: color-mix(in oklab, var(--color-primary) 8%, transparent);
  --scalar-sidebar-item-hover-color: var(--color-foreground);
  --scalar-sidebar-item-active-background: color-mix(in oklab, var(--color-primary) 14%, transparent);
  --scalar-sidebar-indent-border: var(--color-border);
  --scalar-sidebar-indent-border-hover: color-mix(in oklab, var(--color-primary) 40%, transparent);
  --scalar-sidebar-indent-border-active: var(--color-primary);
  --scalar-sidebar-search-background: color-mix(in oklab, var(--color-muted) 55%, var(--color-background));
  --scalar-sidebar-search-border-color: var(--color-border);
  --scalar-sidebar-search-color: var(--color-muted-foreground);
  --scalar-sidebar-font-weight-active: 600;
  --scalar-color-red: var(--destructive);
  --scalar-color-query: var(--color-primary);
}
/* Scalar spends these hues on the method chips AND on every code sample's syntax (a curl's quoted header, a URL's
   port), in the shades the app sets hue text in (text-amber-700 dark:text-amber-400): a 500 on white is 2.1:1 for
   amber. Keyed to the app's \`.dark\`, not Scalar's \`.dark-mode\`, which Scalar also stamps on every request card
   in a light reference — that card is drawn in the app's light tokens, so its code needs the light shades. */
.light-mode, .dark-mode {
  --scalar-color-green: var(--color-emerald-700);
  --scalar-color-blue: var(--color-blue-700);
  --scalar-color-purple: var(--color-violet-700);
  --scalar-color-orange: var(--color-amber-700);
  --scalar-color-yellow: var(--color-amber-700);
}
.dark :is(.light-mode, .dark-mode) {
  --scalar-color-green: var(--color-emerald-400);
  --scalar-color-blue: var(--color-blue-400);
  --scalar-color-purple: var(--color-violet-400);
  --scalar-color-orange: var(--color-amber-400);
  --scalar-color-yellow: var(--color-amber-400);
}
/* A curl's URL is unquoted, so highlight.js reads its port as a number and paints the middle of the URL. */
.hljs.language-curl .hljs-number,
.hljs.language-bash .hljs-number {
  color: inherit;
}
/* Scalar splits every section into two equal columns: prose on the left, the request/response card on the right.
   That 50/50 is right for an operation (a schema next to its example) and wrong for the introduction, where the
   left column carries the whole quickstart and the right one carries a short server/auth card that needs nothing
   like half the page. Reweight that ONE section; the operations keep the even split. Keyed to Scalar's own column
   breakpoint, not the viewport's: under it the columns stack and the card takes the full width. */
@container narrow-references-container (width > 900px) {
  .introduction-section .section-columns > .section-column:first-child {
    flex: 1.75 1 0;
  }
  .introduction-section .section-columns > .section-column:last-child {
    flex: 1 1 0;
    max-width: 22rem;
  }
}
/* The one rule Scalar has no variable for: its sidebar divider, drawn the way the landing draws every hairline —
   1px of --border fading out toward both ends (.fade-rule-bottom, .solutions-grid in app.css). */
@media (width >= 1000px) {
  .t-doc__sidebar {
    position: relative;
  }
  .t-doc__sidebar::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    border-right: 1px solid var(--color-border);
    mask-image: linear-gradient(to bottom, transparent, #000 min(5rem, 22%), #000 calc(100% - min(5rem, 22%)), transparent);
    pointer-events: none;
  }
}`

function RouteComponent() {
  // query: direct a relative-URL fetch with no origin on the server, so it can only run in the browser —
  // there is no trpc key here for a loader to own.
  const { data } = useClientOnlyQuery({
    queryKey: ['docs'],
    queryFn: async () => {
      const response = await fetch('/api/v1/openapi')
      if (!response.ok) {
        throw new Error('Failed to fetch API description')
      }
      return response.text()
    },
  })

  // The page is auth-optional — a logged-out visitor can read the API reference — but the `apps` queries
  // below are not: a logged-out request 401s, which the global tRPC link turns into a /login redirect. They
  // exist only to inject the viewer's own clientSecret into the examples, so they are gated on an
  // authenticated user and an anonymous visitor just sees the `YOUR_SECRET_TOKEN` placeholder.
  const user = useSessionUser()
  // query: direct skipToken-gated on an authenticated viewer; the page itself is auth-optional
  const { data: apps = [] } = useQuery(trpc.apps.getAll.queryOptions(user ? undefined : skipToken))
  const appId = apps[0]?.clientId ?? null
  // query: direct keyed off the app the query above just resolved, so no loader can own it
  const { data: app } = useQuery(trpc.apps.getOne.queryOptions(appId ?? skipToken))
  const { language } = useTranslation()
  const { t } = useLingui()
  const content = withEnglishOnlyNotice(
    data?.replaceAll('YOUR_SECRET_TOKEN', app?.app.clientSecret ?? 'YOUR_SECRET_TOKEN') ?? '{}',
    language === 'en-US' ? undefined : t`The API reference is available in English only.`,
  )

  // Scalar owns a scheme of its own; forcing it from ours is what keeps the reference from rendering light under a
  // dark landing bar when the viewer's app theme and their OS setting disagree.
  const theme = useResolvedTheme()

  // The skeleton stays up until Scalar has mounted: between the route resolving and that, the page waits on the spec
  // fetch AND a ~4MB script off the CDN, and an empty #scalar there read as a blank page.
  const [mounted, setMounted] = useState(false)
  // The script off the CDN can fail (offline, a blocker, a CDN outage): the visitor's environment, not a defect, so
  // it is shown with a way to retry instead of left on the skeleton or reported as an error.
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    // No mounting on the '{}' placeholder: Scalar would paint an empty reference in place of the skeleton.
    if (!theme || !data) return
    let reference: { destroy: () => void } | undefined
    let cancelled = false
    void loadScalar().then(
      () => {
        if (cancelled || !window.Scalar) return
        reference = window.Scalar.createApiReference('#scalar', {
          content,
          customCss,
          showDeveloperTools: 'never',
          forceDarkModeState: theme,
          hideDarkModeToggle: true,
          // Scalar otherwise pulls Inter and JetBrains Mono off its own CDN, overriding the font tokens above.
          withDefaultFonts: false,
          telemetry: false,
          localization: { locale: SCALAR_LOCALES[language] },
          // Scalar's own "Ask AI" and "Generate MCP" sidebar buttons: its hosted features, not part of our API.
          agent: { disabled: true },
          mcp: { disabled: true },
        })
        setMounted(true)
      },
      () => {
        if (!cancelled) setFailed(true)
      },
    )
    return () => {
      cancelled = true
      reference?.destroy()
    }
  }, [content, data, theme, language, attempt])

  return (
    <>
      {failed ? (
        <div className="mx-auto flex min-h-[60dvh] max-w-6xl flex-col items-center justify-center gap-3 px-5 text-center sm:px-6">
          <p className="text-muted-foreground text-sm">
            {t`The API reference couldn't load. Check your connection and try again.`}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              setFailed(false)
              setAttempt((count) => count + 1)
            }}
          >
            {t`Try again`}
          </Button>
        </div>
      ) : (
        !mounted && <DocsPending />
      )}
      {/* Displayed (only unsized) while pending: Scalar mounts into it and measures its sections for the
          scroll-spy and a deep link's jump, which a `display: none` host would hand zero. */}
      <div id="scalar" className={mounted ? 'mx-auto min-h-dvh max-w-6xl px-5 sm:px-6' : undefined} />
    </>
  )
}

/**
 * The spec's prose is written once, in English: it is the API contract, and its field names, enums and examples are
 * English either way. The language selector still translates Scalar's own chrome (search, "Responses", "Copy"...),
 * so outside English the introduction opens by saying plainly that the prose is not translated. It goes INTO the
 * spec rather than above the reference because Scalar scrolls its first section to the top on mount, which put a
 * banner above it under the sticky landing bar.
 */
function withEnglishOnlyNotice(content: string, notice: string | undefined) {
  if (!notice || content === '{}') return content
  const spec = JSON.parse(content) as { info?: { description?: string } }
  if (!spec.info) return content
  spec.info.description = `> ${notice}\n\n${spec.info.description ?? ''}`
  return JSON.stringify(spec)
}

// The nav's rows as Scalar draws them on a bare /docs: the introduction's group, collapsed, then one row per operation
// — its path (in px, a second line where it wraps) and its method chip (GET 18px, POST 24px, PATCH 30px).
const SIDEBAR_ROWS: { lines: number[]; method?: number }[] = [
  { lines: [84] },
  { lines: [27], method: 18 },
  { lines: [81], method: 24 },
  { lines: [108], method: 18 },
  { lines: [108], method: 18 },
  { lines: [108], method: 30 },
  { lines: [35], method: 24 },
  { lines: [62], method: 18 },
  { lines: [62], method: 30 },
  { lines: [62], method: 18 },
  { lines: [94], method: 18 },
  { lines: [110], method: 18 },
  { lines: [144], method: 18 },
  { lines: [163], method: 18 },
  { lines: [197], method: 18 },
  { lines: [193], method: 18 },
  { lines: [193, 34], method: 18 },
  { lines: [138, 64], method: 24 },
  { lines: [202], method: 18 },
  { lines: [76], method: 24 },
  { lines: [103], method: 18 },
  { lines: [103], method: 18 },
]

/**
 * Scalar's own layout as a skeleton, measured off the mounted reference so it lands without a shift: the 17rem
 * sidebar (search, the nav's 33px rows, the client link and credit at its foot) beside the introduction. At the landing bar's width the reference is never
 * past Scalar's 900px container breakpoint, so the introduction is ONE column — its request card stacks under the
 * prose, far below the fold — and the skeleton draws only that column: the version badges, title, download link,
 * lead paragraph, base URL block, then "Getting started".
 */
function DocsPending() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-6xl px-5 sm:px-6" aria-busy>
      {/* Sticky under the bar and one viewport tall, as Scalar's is, so its footer lands where Scalar's does. */}
      <div className="sticky top-17 hidden h-[calc(100dvh-4.25rem)] w-68 shrink-0 flex-col p-3 min-[1000px]:flex">
        <Skeleton className="h-8 w-61.75" />
        <div className="mt-3 flex min-h-0 flex-1 flex-col gap-px overflow-hidden">
          {SIDEBAR_ROWS.map((row, index) => (
            // oxlint-disable-next-line react/no-array-index-key -- a fixed list that never reorders
            <div key={index} className="flex shrink-0 items-start py-2 pr-2 pl-2">
              <div className="flex flex-col">
                {row.lines.map((width) => (
                  <div key={width} className="flex h-4 items-center">
                    <Skeleton className="h-3.5" style={{ width }} />
                  </div>
                ))}
              </div>
              {row.method && <Skeleton className="mt-0.5 ml-auto h-2.75 shrink-0" style={{ width: row.method }} />}
            </div>
          ))}
        </div>
        <Skeleton className="mt-3 h-7.75 w-61.75" />
        <Skeleton className="mt-3 h-3.75 w-28.5" />
        <div
          className="absolute inset-y-0 right-0 border-r"
          style={{
            maskImage:
              'linear-gradient(to bottom, transparent, #000 min(5rem, 22%), #000 calc(100% - min(5rem, 22%)), transparent)',
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        {/* Under 1000px Scalar folds the sidebar into a bar above the content: a menu toggle and the section's name. */}
        <div className="flex h-12.5 items-center gap-3 border-b px-5 min-[1000px]:hidden">
          <Skeleton className="size-4" />
          <Skeleton className="h-3.5 w-20" />
        </div>
        <div className="px-6 py-12">
          <div className="flex gap-1.5">
            <Skeleton className="h-5.25 w-13 rounded-2xl" />
            <Skeleton className="h-5.25 w-25 rounded-2xl" />
          </div>
          <Skeleton className="mt-0.5 h-7.5 w-44" />
          <Skeleton className="mt-3.5 h-4 w-60" />
          <div className="mt-8.5 flex flex-col gap-2.5">
            {[100, 98, 64].map((width) => (
              <Skeleton key={width} className="h-4" style={{ width: `${width}%` }} />
            ))}
          </div>
          <Skeleton className="mt-6.5 h-4 w-1/2" />
          <Skeleton className="mt-5 h-11.25 w-full rounded-lg" />
          <Skeleton className="mt-9 h-7 w-48" />
          <div className="mt-4 flex flex-col gap-2.5">
            {[100, 99, 98].map((width) => (
              <Skeleton key={width} className="h-4" style={{ width: `${width}%` }} />
            ))}
          </div>
          <Skeleton className="mt-6.5 h-4 w-32" />
          {/* The five-step flow: each step's lines, as they wrap at the bar's full width. */}
          <div className="mt-7 flex flex-col gap-3.5 pl-8">
            {[[91], [94, 8], [91, 15], [97, 10], [92]].map((lines) => (
              <div key={lines.join()} className="flex flex-col gap-2.5">
                {lines.map((width) => (
                  <Skeleton key={width} className="h-4" style={{ width: `${width}%` }} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
